package com.checkin.shared.dto

import com.checkin.shared.PropertyTimeDefaults
import com.checkin.shared.model.GuestStatus
import com.checkin.shared.model.GuestVerificationStep
import com.checkin.shared.model.PropertyStatus
import com.checkin.shared.model.PropertyType
import com.checkin.shared.model.ReservationStatus
import com.checkin.shared.model.StepStatus
import kotlinx.serialization.Serializable

@Serializable
data class RegisterRequest(
    val email: String,
    val password: String,
    val displayName: String,
    val acceptTerms: Boolean = false,
    val acceptPrivacy: Boolean = false,
)

@Serializable
data class LoginRequest(
    val email: String,
    val password: String,
)

@Serializable
data class GoogleAuthRequest(
    val idToken: String,
    val acceptTerms: Boolean = false,
    val acceptPrivacy: Boolean = false,
)

@Serializable
data class AuthResponse(
    val token: String,
    val user: UserDto,
)

@Serializable
data class RegisterResponse(
    val message: String? = null,
    val email: String? = null,
    val token: String? = null,
    val user: UserDto? = null,
)

@Serializable
data class PublicAppConfigDto(
    val registrationEnabled: Boolean = true,
)

@Serializable
data class MessageResponse(
    val message: String,
)

@Serializable
data class ContactFormRequest(
    val name: String,
    val email: String,
    val message: String,
    /** Honeypot anti-spam — doit rester vide. */
    val website: String = "",
)

@Serializable
data class SubscriptionDto(
    val plan: String,
    val status: String,
    val propertiesLimit: Int,
    val propertiesUsed: Int,
    val freeCheckinsRemaining: Int,
    val checkoutAvailable: Boolean,
    val canManageSubscription: Boolean = false,
    val propertiesOverLimit: Int = 0,
    val planDowngradeDeadline: String? = null,
    val excessPropertyIds: List<String> = emptyList(),
)

@Serializable
data class CreateCheckoutRequest(
    val plan: String,
)

@Serializable
data class CheckoutResponse(
    val url: String,
)

@Serializable
data class ForgotPasswordRequest(
    val email: String,
)

@Serializable
data class ResetPasswordRequest(
    val token: String,
    val newPassword: String,
)

@Serializable
data class ResendVerificationRequest(
    val email: String,
)

@Serializable
data class UserDto(
    val id: String,
    val email: String,
    val displayName: String,
    val contractLandlordName: String? = null,
    val hasHostSignature: Boolean = false,
    val hasHostLogo: Boolean = false,
    val hasHostStamp: Boolean = false,
    /** false = compte Google sans mot de passe local */
    val hasPassword: Boolean = true,
)

@Serializable
data class UpdateProfileRequest(
    val displayName: String? = null,
    val email: String? = null,
    val password: String? = null,
    val contractLandlordName: String? = null,
    /** null = ne pas modifier ; chaîne vide = supprimer la signature */
    val hostSignatureBase64: String? = null,
    /** null = ne pas modifier ; chaîne vide = supprimer le logo */
    val hostLogoBase64: String? = null,
    /** null = ne pas modifier ; chaîne vide = supprimer le cachet */
    val hostStampBase64: String? = null,
)

@Serializable
data class PropertyDto(
    val id: String,
    val name: String,
    val address: String,
    val city: String,
    val type: PropertyType,
    val photoUrl: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val status: PropertyStatus = PropertyStatus.ACTIVE,
    val checkInTime: String = PropertyTimeDefaults.CHECK_IN,
    val checkOutTime: String = PropertyTimeDefaults.CHECK_OUT,
    /** Bien actif hors quota du plan (downgrade en cours). */
    val quotaExcess: Boolean = false,
    /** Sera archivé automatiquement si le délai downgrade expire. */
    val autoArchiveScheduled: Boolean = false,
)

@Serializable
data class CreatePropertyRequest(
    val name: String,
    val address: String,
    val city: String,
    val type: PropertyType = PropertyType.APPARTEMENT,
    val photoUrl: String? = null,
    val photoContentBase64: String? = null,
    val photoFileName: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val checkInTime: String = PropertyTimeDefaults.CHECK_IN,
    val checkOutTime: String = PropertyTimeDefaults.CHECK_OUT,
)

@Serializable
data class UpdatePropertyRequest(
    val name: String? = null,
    val address: String? = null,
    val city: String? = null,
    val type: PropertyType? = null,
    val photoUrl: String? = null,
    val photoContentBase64: String? = null,
    val photoFileName: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val checkInTime: String? = null,
    val checkOutTime: String? = null,
)

@Serializable
data class VerificationConfigDto(
    val documentEnabled: Boolean = true,
    val selfieEnabled: Boolean = true,
    val contractEnabled: Boolean = true,
    val customMessage: String? = null,
)

@Serializable
data class GuestDto(
    val id: String,
    val guestIndex: Int,
    val isPrimary: Boolean,
    val fullName: String? = null,
    val status: GuestStatus,
    val checkInUrl: String,
    val manualVerificationRequired: Boolean = false,
)

@Serializable
data class VerificationMediaFileDto(
    val storageKey: String,
    val category: String,
    val attemptId: String,
    val fileName: String,
    val capturedAt: String? = null,
)

@Serializable
data class GuestContractFieldsDto(
    val fullName: String = "",
    val idNumber: String = "",
    val idExpiry: String? = null,
    val nationality: String? = null,
)

@Serializable
data class UpdateGuestContractFieldsRequest(
    val fullName: String,
    val idNumber: String,
    val idExpiry: String? = null,
    val nationality: String? = null,
)

@Serializable
data class GuestVerificationMediaDto(
    val guestId: String,
    val guestIndex: Int,
    val guestName: String? = null,
    val status: GuestStatus,
    val manualVerificationRequired: Boolean = false,
    val awaitingHostReview: Boolean = false,
    val signedContractAvailable: Boolean = false,
    val checkInUrl: String = "",
    val contractFields: GuestContractFieldsDto? = null,
    val documents: List<VerificationMediaFileDto> = emptyList(),
    val selfies: List<VerificationMediaFileDto> = emptyList(),
    val verificationPurged: Boolean = false,
    val contractPurged: Boolean = false,
)

@Serializable
data class ExportedMediaFileDto(
    val fileName: String,
    val category: String,
    val capturedAt: String? = null,
    val contentBase64: String,
)

@Serializable
data class GuestPersonalDataExportDto(
    val exportedAt: String,
    val guestId: String,
    val guestIndex: Int,
    val guestName: String? = null,
    val status: GuestStatus,
    val idNumber: String? = null,
    val idExpiry: String? = null,
    val nationality: String? = null,
    val faceMatchScore: Double? = null,
    val consentGivenAt: String? = null,
    val consentVersion: String? = null,
    val verificationPurgedAt: String? = null,
    val contractPurgedAt: String? = null,
    val documents: List<ExportedMediaFileDto> = emptyList(),
    val selfies: List<ExportedMediaFileDto> = emptyList(),
    val signedContractBase64: String? = null,
)

@Serializable
data class PurgePersonalDataResponse(
    val message: String,
    val purgedAt: String,
)

@Serializable
data class ReservationVerificationMediaDto(
    val reservationId: String,
    val referenceManual: String,
    val guests: List<GuestVerificationMediaDto> = emptyList(),
)

@Serializable
data class ReservationDto(
    val id: String,
    val referenceManual: String,
    val propertyId: String,
    val propertyName: String,
    val hostName: String,
    val checkIn: String,
    val checkOut: String,
    val guestCount: Int,
    val accessCode: String? = null,
    val status: ReservationStatus,
    val verificationConfig: VerificationConfigDto,
    val contractTemplateId: String? = null,
    val guests: List<GuestDto> = emptyList(),
    val primaryGuestEmail: String? = null,
)

@Serializable
data class CreateReservationRequest(
    val propertyId: String,
    val referenceManual: String,
    val checkIn: String,
    val checkOut: String,
    val guestCount: Int,
    val primaryGuestName: String,
    val primaryGuestEmail: String? = null,
    val accessCode: String? = null,
    val verificationConfig: VerificationConfigDto = VerificationConfigDto(),
    val contractTemplateId: String? = null,
)

@Serializable
data class UpdateReservationRequest(
    val referenceManual: String? = null,
    val checkIn: String? = null,
    val checkOut: String? = null,
    val guestCount: Int? = null,
    val primaryGuestName: String? = null,
    val primaryGuestEmail: String? = null,
    val accessCode: String? = null,
    val status: ReservationStatus? = null,
    val verificationConfig: VerificationConfigDto? = null,
    val contractTemplateId: String? = null,
)

@Serializable
data class DashboardStatsDto(
    val pendingReservations: Int,
    val completedVerifications: Int,
    val guestsInProgress: Int,
    /** Taux d'occupation du parc actif sur le mois en cours (0–100). */
    val occupancyRatePercent: Int,
    /** Durée moyenne de séjour en nuits (réservations non annulées). */
    val averageStayDays: Double,
    /** Nombre d'arrivées (check-in) sur le mois en cours. */
    val checkInsThisMonth: Int,
    /** Mois de référence pour occupation et check-ins (YYYY-MM). */
    val statsMonth: String,
)

@Serializable
data class CalendarDayDto(
    val date: String,
    val checkIns: Int,
    val checkOuts: Int,
)

@Serializable
data class ApiError(
    val error: String,
)

@Serializable
data class VerificationLinkResponse(
    val url: String,
    val whatsAppUrl: String,
    val primaryGuestEmail: String? = null,
)

@Serializable
data class ContractTemplateDto(
    val id: String,
    val name: String,
    val isSystemDefault: Boolean,
    val bodyText: String,
    val createdAt: String,
)

@Serializable
data class SaveContractTemplateRequest(
    val name: String,
    val bodyText: String,
)

@Serializable
data class UpdateContractTemplateRequest(
    val name: String? = null,
    val bodyText: String? = null,
)

@Serializable
data class CalendarMonthDto(
    val month: String,
    val days: List<CalendarDayDto>,
)

@Serializable
data class GuestPropertyDto(
    val name: String,
    val address: String,
    val city: String,
    val checkInTime: String = PropertyTimeDefaults.CHECK_IN,
    val checkOutTime: String = PropertyTimeDefaults.CHECK_OUT,
    val photoUrl: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
)

@Serializable
data class GuestReservationInfoDto(
    val referenceManual: String,
    val checkIn: String,
    val checkOut: String,
    val hostName: String,
    val guestCount: Int,
)

@Serializable
data class GuestCheckInLinkDto(
    val guestIndex: Int,
    val label: String,
    val url: String,
    val status: GuestStatus,
)

@Serializable
data class GuestStepsDto(
    val document: StepStatus,
    val selfie: StepStatus,
    val contract: StepStatus,
)

@Serializable
data class GuestSessionDto(
    val token: String,
    val guestId: String,
    val guestIndex: Int,
    val isPrimary: Boolean,
    val guestName: String?,
    val status: GuestStatus,
    val currentStep: GuestVerificationStep,
    val steps: GuestStepsDto,
    val property: GuestPropertyDto,
    val reservation: GuestReservationInfoDto,
    val verificationConfig: VerificationConfigDto,
    val idNumberMasked: String? = null,
    val faceMatchScore: Double? = null,
    val accessCode: String? = null,
    val signedContractAvailable: Boolean = false,
    val allGuestsCompleted: Boolean = false,
    val otherGuestLinks: List<GuestCheckInLinkDto> = emptyList(),
    val manualVerificationRequired: Boolean = false,
    /** true = selfie via AWS Face Liveness (vidéo), pas le flux 4 photos. */
    val awsLivenessEnabled: Boolean = false,
    val consentGiven: Boolean = false,
    val consentVersion: String? = null,
)

@Serializable
data class UpdateGuestNameRequest(
    val fullName: String,
)

@Serializable
data class SubmitGuestConsentRequest(
    val accepted: Boolean = true,
    val version: String,
)

@Serializable
data class UploadImageRequest(
    val contentBase64: String,
    val fileName: String = "image.jpg",
)

@Serializable
data class SubmitDocumentRequest(
    val contentBase64: String,
    val fileName: String = "document.jpg",
    val documentType: String = "passport",
    /** Verso CIN (MRZ) — requis si documentType = cin_maroc */
    val contentBase64Back: String? = null,
    val fileNameBack: String? = null,
)

@Serializable
data class LivenessFrameDto(
    val contentBase64: String,
    val fileName: String = "frame.jpg",
    val poseHint: String? = null,
)

@Serializable
data class SubmitLivenessRequest(
    val frames: List<LivenessFrameDto>,
)

@Serializable
data class SignContractRequest(
    val signatureBase64: String? = null,
    val accepted: Boolean = true,
)

@Serializable
data class VerificationStepResultDto(
    val success: Boolean,
    val message: String? = null,
    val session: GuestSessionDto,
)

@Serializable
data class LivenessSessionDto(
    val sessionId: String,
    val region: String,
    val cognitoIdentityPoolId: String,
)

@Serializable
data class CompleteLivenessRequest(
    val sessionId: String,
)
